import { useCallback, useEffect, useRef, useState } from "react";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { ProgressSpinner } from "primereact/progressspinner";
import { Toast } from "primereact/toast";
import { useGetBoMeQuery } from "../../store/services/api";

const AGENT_URL = process.env.REACT_APP_AGENT_WORKER_URL;
const AGENT_API_KEY = process.env.REACT_APP_AGENT_WORKER_API_KEY;

interface Exemplar {
  id: string;
  question: string;
  sql: string;
  notes: string | null;
  source: "manual" | "feedback";
  active: boolean;
  createdAt: string;
}

interface Candidate {
  logId: string;
  question: string;
  sql: string | null;
  isHelpful: boolean;
  hasCorrection: boolean;
  createdAt: string;
}

interface GlossaryEntry {
  id: string;
  term: string;
  meaning: string;
  active: boolean;
  actor: string | null;
  createdAt: string;
}

/** Borrador del diálogo: un candidato ya editado, o un ejemplo nuevo desde cero. */
interface Draft {
  question: string;
  sql: string;
  notes: string;
  sourceLogId?: string;
}

const agentFetch = (path: string, init?: RequestInit) =>
  fetch(`${AGENT_URL}${path}`, {
    ...init,
    headers: {
      "Content-Type": "application/json",
      "Authorization": `Bearer ${AGENT_API_KEY ?? ""}`,
      ...(init?.headers ?? {}),
    },
  });

const formatDate = (iso: string) => new Date(iso).toLocaleDateString("es-AR");

export const AgentExemplars = () => {
  // Misma restricción que el agente de consultas: la curación define qué aprende el agente.
  const { data: me, isLoading: meLoading } = useGetBoMeQuery();

  const [exemplars, setExemplars] = useState<Exemplar[]>([]);
  const [candidates, setCandidates] = useState<Candidate[]>([]);
  const [glossary, setGlossary] = useState<GlossaryEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [draft, setDraft] = useState<Draft | null>(null);
  const toast = useRef<Toast>(null);

  const notify = (severity: "success" | "error", detail: string) =>
    toast.current?.show({ severity, summary: severity === "success" ? "Listo" : "Error", detail });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [libRes, candRes, glosRes] = await Promise.all([
        agentFetch("/exemplars"),
        agentFetch("/exemplars/candidates"),
        agentFetch("/glossary"),
      ]);
      if (!libRes.ok || !candRes.ok || !glosRes.ok) {
        throw new Error("No se pudo cargar lo que aprendió el agente");
      }
      setExemplars((await libRes.json()).exemplars ?? []);
      setCandidates((await candRes.json()).candidates ?? []);
      setGlossary((await glosRes.json()).glossary ?? []);
    } catch (e) {
      notify("error", e instanceof Error ? e.message : "Error desconocido");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (me?.role === "SUPER_ADMIN") load();
  }, [me?.role, load]);

  const save = async () => {
    if (!draft) return;
    setSaving(true);
    try {
      const res = await agentFetch("/exemplars", {
        method: "POST",
        body: JSON.stringify({
          question: draft.question.trim(),
          sql: draft.sql.trim(),
          notes: draft.notes.trim() || undefined,
          sourceLogId: draft.sourceLogId,
        }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.details ?? body.error ?? `Error ${res.status}`);
      }
      setDraft(null);
      notify("success", "Ejemplo agregado. El agente ya lo usa en las próximas consultas.");
      await load();
    } catch (e) {
      notify("error", e instanceof Error ? e.message : "Error desconocido");
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (ex: Exemplar) => {
    try {
      const res = await agentFetch(`/exemplars/${ex.id}`, {
        method: "PATCH",
        body: JSON.stringify({ active: !ex.active }),
      });
      if (!res.ok) throw new Error(`Error ${res.status}`);
      setExemplars((prev) =>
        prev.map((e) => (e.id === ex.id ? { ...e, active: !e.active } : e))
      );
    } catch (e) {
      notify("error", e instanceof Error ? e.message : "Error desconocido");
    }
  };

  const toggleTerm = async (entry: GlossaryEntry) => {
    try {
      const res = await agentFetch(`/glossary/${entry.id}`, {
        method: "PATCH",
        body: JSON.stringify({ active: !entry.active }),
      });
      if (!res.ok) throw new Error(`Error ${res.status}`);
      setGlossary((prev) =>
        prev.map((g) => (g.id === entry.id ? { ...g, active: !g.active } : g))
      );
    } catch (e) {
      notify("error", e instanceof Error ? e.message : "Error desconocido");
    }
  };

  const removeTerm = async (entry: GlossaryEntry) => {
    if (!window.confirm(`¿Borrar "${entry.term}" del glosario?`)) return;
    try {
      const res = await agentFetch(`/glossary/${entry.id}`, { method: "DELETE" });
      if (!res.ok) throw new Error(`Error ${res.status}`);
      setGlossary((prev) => prev.filter((g) => g.id !== entry.id));
      notify("success", "Término borrado");
    } catch (e) {
      notify("error", e instanceof Error ? e.message : "Error desconocido");
    }
  };

  const remove = async (ex: Exemplar) => {
    if (!window.confirm("¿Borrar este ejemplo? El agente deja de usarlo.")) return;
    try {
      const res = await agentFetch(`/exemplars/${ex.id}`, { method: "DELETE" });
      if (!res.ok) throw new Error(`Error ${res.status}`);
      setExemplars((prev) => prev.filter((e) => e.id !== ex.id));
      notify("success", "Ejemplo borrado");
    } catch (e) {
      notify("error", e instanceof Error ? e.message : "Error desconocido");
    }
  };

  if (meLoading) return <div className="p-4">Cargando…</div>;
  if (me?.role !== "SUPER_ADMIN") {
    return (
      <div className="p-4">
        <div className="p-3 border-round surface-200 text-700">
          Acceso restringido: la curación de ejemplos está disponible solo para SUPER_ADMIN.
        </div>
      </div>
    );
  }

  const activeCount = exemplars.filter((e) => e.active).length;

  return (
    <div className="p-card gap-4 p-3">
      <Toast ref={toast} />

      <div className="flex w-full justify-content-between align-items-center gap-3 mt-4 mb-2 flex-wrap">
        <h1 className="m-0">Lo que aprendió el agente</h1>
        <div className="flex gap-2">
          <Button
            icon="pi pi-refresh"
            label="Actualizar"
            severity="secondary"
            outlined
            size="small"
            onClick={load}
            disabled={loading}
          />
          <Button
            icon="pi pi-plus"
            label="Nuevo ejemplo"
            size="small"
            onClick={() => setDraft({ question: "", sql: "", notes: "" })}
          />
        </div>
      </div>

      <p className="text-color-secondary mt-0 mb-4" style={{ maxWidth: "70ch" }}>
        Cada ejemplo es un par pregunta→SQL verificado por una persona. Ante una consulta parecida, el
        agente los recibe como referencia y copia su forma. Un ejemplo mal armado se propaga a todas las
        consultas siguientes, así que conviene revisar el SQL antes de agregarlo.{" "}
        <strong>{activeCount} activo{activeCount !== 1 ? "s" : ""}</strong> de {exemplars.length}.
      </p>

      {loading && (
        <div className="flex justify-content-center p-5">
          <ProgressSpinner style={{ width: "36px", height: "36px" }} strokeWidth="4" />
        </div>
      )}

      {!loading && (
        <>
          {/* Candidatos: respuestas ya votadas que nadie promovió todavía */}
          <section className="mb-5">
            <h2 className="text-lg font-medium mb-1">
              Pendientes de revisar{" "}
              <span className="text-color-secondary font-normal">({candidates.length})</span>
            </h2>
            <p className="text-sm text-color-secondary mt-0 mb-3">
              Respuestas que recibieron 👍, o un 👎 con el SQL corregido.
            </p>

            {candidates.length === 0 ? (
              <div className="p-3 border-round surface-100 text-color-secondary text-sm">
                No hay nada pendiente. Votá respuestas desde la pantalla de Consultas y aparecen acá.
              </div>
            ) : (
              <div className="flex flex-column gap-2">
                {candidates.map((c) => (
                  <div
                    key={c.logId}
                    className="border-1 surface-border border-round p-3 flex align-items-start justify-content-between gap-3 flex-wrap"
                  >
                    <div style={{ minWidth: "260px", flex: 1 }}>
                      <div className="flex align-items-center gap-2 mb-1">
                        <i
                          className={`pi ${c.isHelpful ? "pi-thumbs-up text-green-600" : "pi-thumbs-down text-orange-600"}`}
                          style={{ fontSize: ".8rem" }}
                        />
                        <span className="font-medium">{c.question}</span>
                        {c.hasCorrection && (
                          <span className="text-xs px-2 py-1 border-round surface-200 text-color-secondary">
                            SQL corregido
                          </span>
                        )}
                      </div>
                      <pre
                        className="m-0 p-2 border-round surface-100 overflow-x-auto"
                        style={{ fontSize: ".75rem", lineHeight: 1.5 }}
                      >
                        {c.sql}
                      </pre>
                      <span className="text-xs text-color-secondary">{formatDate(c.createdAt)}</span>
                    </div>
                    <Button
                      icon="pi pi-check"
                      label="Revisar y promover"
                      size="small"
                      outlined
                      onClick={() =>
                        setDraft({
                          question: c.question,
                          sql: c.sql ?? "",
                          notes: "",
                          sourceLogId: c.logId,
                        })
                      }
                    />
                  </div>
                ))}
              </div>
            )}
          </section>

          {/* Glosario: lo que el agente aprendió por corrección en el chat */}
          <section className="mb-5">
            <h2 className="text-lg font-medium mb-1">
              Glosario{" "}
              <span className="text-color-secondary font-normal">({glossary.length})</span>
            </h2>
            <p className="text-sm text-color-secondary mt-0 mb-3">
              Vocabulario que el equipo le enseñó corrigiéndolo en el chat. Viaja entero en cada
              consulta, así que conviene tenerlo corto y sin contradicciones.
            </p>

            {glossary.length === 0 ? (
              <div className="p-3 border-round surface-100 text-color-secondary text-sm">
                Vacío. Corregilo desde el chat: “cuando digo comunidad ub me refiero al tenant
                clubUb”.
              </div>
            ) : (
              <div className="flex flex-column gap-2">
                {glossary.map((g) => (
                  <div
                    key={g.id}
                    className="border-1 surface-border border-round p-3 flex align-items-center justify-content-between gap-3 flex-wrap"
                    style={{ opacity: g.active ? 1 : 0.55 }}
                  >
                    <div style={{ minWidth: "240px", flex: 1 }}>
                      <span className="font-medium">{g.term}</span>
                      <span className="text-color-secondary"> → {g.meaning}</span>
                      {!g.active && (
                        <span className="text-xs px-2 py-1 ml-2 border-round surface-200 text-color-secondary">
                          inactivo
                        </span>
                      )}
                    </div>
                    <div className="flex gap-1">
                      <Button
                        icon={g.active ? "pi pi-eye-slash" : "pi pi-eye"}
                        tooltip={g.active ? "Desactivar" : "Activar"}
                        tooltipOptions={{ position: "left" }}
                        size="small"
                        text
                        severity="secondary"
                        onClick={() => toggleTerm(g)}
                      />
                      <Button
                        icon="pi pi-trash"
                        tooltip="Borrar"
                        tooltipOptions={{ position: "left" }}
                        size="small"
                        text
                        severity="danger"
                        onClick={() => removeTerm(g)}
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>

          {/* Biblioteca */}
          <section>
            <h2 className="text-lg font-medium mb-3">
              Biblioteca <span className="text-color-secondary font-normal">({exemplars.length})</span>
            </h2>

            {exemplars.length === 0 ? (
              <div className="p-3 border-round surface-100 text-color-secondary text-sm">
                Todavía no hay ejemplos. El agente responde solo con el schema.
              </div>
            ) : (
              <div className="flex flex-column gap-2">
                {exemplars.map((ex) => (
                  <div
                    key={ex.id}
                    className="border-1 surface-border border-round p-3 flex align-items-start justify-content-between gap-3 flex-wrap"
                    style={{ opacity: ex.active ? 1 : 0.55 }}
                  >
                    <div style={{ minWidth: "260px", flex: 1 }}>
                      <div className="flex align-items-center gap-2 mb-1 flex-wrap">
                        <span className="font-medium">{ex.question}</span>
                        <span className="text-xs px-2 py-1 border-round surface-200 text-color-secondary">
                          {ex.source === "feedback" ? "desde feedback" : "manual"}
                        </span>
                        {!ex.active && (
                          <span className="text-xs px-2 py-1 border-round surface-200 text-color-secondary">
                            inactivo
                          </span>
                        )}
                      </div>
                      <pre
                        className="m-0 p-2 border-round surface-100 overflow-x-auto"
                        style={{ fontSize: ".75rem", lineHeight: 1.5 }}
                      >
                        {ex.sql}
                      </pre>
                      {ex.notes && (
                        <p className="m-0 mt-1 text-sm text-color-secondary">{ex.notes}</p>
                      )}
                    </div>
                    <div className="flex gap-1">
                      <Button
                        icon={ex.active ? "pi pi-eye-slash" : "pi pi-eye"}
                        tooltip={ex.active ? "Desactivar" : "Activar"}
                        tooltipOptions={{ position: "left" }}
                        size="small"
                        text
                        severity="secondary"
                        onClick={() => toggleActive(ex)}
                      />
                      <Button
                        icon="pi pi-trash"
                        tooltip="Borrar"
                        tooltipOptions={{ position: "left" }}
                        size="small"
                        text
                        severity="danger"
                        onClick={() => remove(ex)}
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>
        </>
      )}

      <Dialog
        header={draft?.sourceLogId ? "Promover a ejemplo" : "Nuevo ejemplo"}
        visible={draft !== null}
        onHide={() => setDraft(null)}
        style={{ width: "min(720px, 94vw)" }}
      >
        {draft && (
          <div className="flex flex-column gap-3 pt-2">
            <div>
              <label className="block text-sm font-medium mb-1">Pregunta</label>
              <InputText
                value={draft.question}
                onChange={(e) => setDraft({ ...draft, question: e.target.value })}
                className="w-full"
                placeholder="Cómo la escribiría alguien del equipo"
              />
              <small className="text-color-secondary">
                Es lo que se compara contra la consulta entrante. Conviene dejarla natural y genérica.
              </small>
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">SQL</label>
              <InputTextarea
                value={draft.sql}
                onChange={(e) => setDraft({ ...draft, sql: e.target.value })}
                rows={10}
                className="w-full"
                style={{ fontFamily: "monospace", fontSize: ".8rem" }}
              />
              <small className="text-color-secondary">
                Revisalo antes de guardar. Los datos personales del log vienen redactados como
                <code> [email]</code> o <code>[number]</code>: reemplazalos por un valor de ejemplo
                razonable o sacá el filtro.
              </small>
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Nota (opcional)</label>
              <InputText
                value={draft.notes}
                onChange={(e) => setDraft({ ...draft, notes: e.target.value })}
                className="w-full"
                placeholder="Por qué este ejemplo importa"
              />
            </div>

            <div className="flex justify-content-end gap-2">
              <Button
                label="Cancelar"
                severity="secondary"
                outlined
                onClick={() => setDraft(null)}
                disabled={saving}
              />
              <Button
                label="Guardar ejemplo"
                onClick={save}
                loading={saving}
                disabled={!draft.question.trim() || !draft.sql.trim()}
              />
            </div>
          </div>
        )}
      </Dialog>
    </div>
  );
};
